import { useCallback, useEffect, useState } from 'react';
import { Account, ChangeLog, PermissionEnum, ResponseError } from '@ui/src';
import {
  useLazyGetCustomerAccountClosingBalancesQuery,
  useLazyGetCustomersAccountsChangeLogsQuery,
  useUpdateCustomersOverdraftStatusMutation,
} from '../../../customers/store/services/customers';
import { Balance } from '../../types';

const useAccount = ({
  customerId,
  blockCreditIsSuccess,
  unblockCreditIsSuccess,
  blockDebitIsSuccess,
  unblockDebitIsSuccess,
  closeIsSuccess,
  openIsSuccess,
  account,
  overdraftEnabled,
  activePermissions,
  accountsLogs,
  setBalances,
  setDefaultBalances,
  accountsBalances,
  balance,
  selectedAccount,
}: {
  customerId?: string;
  unblockCreditIsSuccess?: boolean;
  blockCreditIsSuccess?: boolean;
  blockDebitIsSuccess?: boolean;
  unblockDebitIsSuccess?: boolean;
  closeIsSuccess?: boolean;
  openIsSuccess?: boolean;
  account?: Account & { visibleToClient: boolean; overdraftEnabled?: boolean };
  overdraftEnabled?: boolean;
  activePermissions?: PermissionEnum[];
  accountsLogs?: ChangeLog[];
  setBalances: (data: Balance[]) => void;
  setDefaultBalances: (data: Balance[]) => void;
  accountsBalances?: Balance[];
  balance?: Balance[];
  selectedAccount?: string;
}) => {
  const [blockWithdrawPopupVisible, setBlockWithdrawPopupVisible] =
    useState(false);
  const [blockTopupPopupVisible, setBlockTopupPopupVisible] = useState(false);
  const [closePopupVisible, setClosePopupVisible] = useState(false);
  const [creditBlock, setCreditBlock] = useState(false);
  const [debitBlock, setDebitBlock] = useState(false);
  const [closed, setClosed] = useState(false);
  const [comment, setComment] = useState('');
  const [settingsPanelVisible, setSettingsPanelVisible] = useState(false);
  const [visibleToClient, setVisibleToClient] = useState(false);
  const [selectedTab, setSelectedTab] = useState('settings');
  // const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountOverdraftEnabled, setOverdraftEnabled] = useState(false);
  const [closingBalancesAt, setClosingBalancesAt] = useState('');

  const [updateOverdraftStatus, { isLoading: overdraftUpdateInProgress }] =
    useUpdateCustomersOverdraftStatusMutation();
  const [
    getCustomerAccountClosingBalances,
    { isLoading: balancesIsLoading, isFetching: balancesIsFetching },
  ] = useLazyGetCustomerAccountClosingBalancesQuery();
  const [getAccountsLogs, { isLoading, isFetching }] =
    useLazyGetCustomersAccountsChangeLogsQuery();

  const getCustomerAccountClosingBalancesAction = async ({
    accountId,
    date,
  }: {
    accountId: string;
    date?: string;
  }) => {
    const body: {
      id: string;
      closingBalancesAt?: string;
    } = {
      id: accountId,
    };

    if (date) {
      body.closingBalancesAt = date;
    }

    await getCustomerAccountClosingBalances(
      `?accountId=${body.id}&date=${body.closingBalancesAt}`,
    );
  };

  const onUpdateOverdraftStatus = async ({
    enabled,
    accountId,
  }: {
    enabled: boolean;
    accountId: string;
  }) => {
    setOverdraftEnabled(enabled);

    const response = (await updateOverdraftStatus({
      id: customerId as string,
      accountId,
      overdraftEnabled: enabled,
    })) as { error?: ResponseError };

    if (response.error) {
      setOverdraftEnabled(!enabled);
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getAccountsLogs(
        `?take=20&entityType=ACCOUNT&entityId=${selectedAccount}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [selectedAccount],
  );

  useEffect(() => {
    if (blockCreditIsSuccess || unblockCreditIsSuccess) {
      setBlockTopupPopupVisible(false);
    }
  }, [blockCreditIsSuccess, unblockCreditIsSuccess]);

  useEffect(() => {
    if (blockDebitIsSuccess || unblockDebitIsSuccess) {
      setBlockWithdrawPopupVisible(false);
    }
  }, [blockDebitIsSuccess, unblockDebitIsSuccess]);

  useEffect(() => {
    if (closeIsSuccess || openIsSuccess) {
      setClosePopupVisible(false);
      setComment('');
    }
  }, [closeIsSuccess, openIsSuccess]);

  useEffect(() => {
    if (account && account.overdraftEnabled && overdraftEnabled) {
      setOverdraftEnabled(true);
    }
  }, [account, settingsPanelVisible, overdraftEnabled]);

  useEffect(() => {
    if (
      selectedTab === 'logs' &&
      !accountsLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      settingsPanelVisible
    ) {
      getChangeLogsAction();
    }
  }, [selectedTab, settingsPanelVisible]);

  useEffect(() => {
    if (balance && settingsPanelVisible && !accountsBalances) {
      setBalances(balance);

      setDefaultBalances(balance);
    }
  }, [settingsPanelVisible, balance, accountsBalances]);

  useEffect(() => {
    if (account) {
      setDebitBlock(account.debitBlock);
      setCreditBlock(account.creditBlock);
      setClosed(account.closed);
      setVisibleToClient(account.visibleToClient);
    }
  }, [account]);

  return {
    accountOverdraftEnabled,
    setSettingsPanelVisible,
    // setSelectedAccountId,
    settingsPanelVisible,
    setSelectedTab,
    setOverdraftEnabled,
    setClosingBalancesAt,
    selectedTab,
    creditBlock,
    setCreditBlock,
    setBlockTopupPopupVisible,
    debitBlock,
    setDebitBlock,
    setBlockWithdrawPopupVisible,
    setVisibleToClient,
    visibleToClient,
    closingBalancesAt,
    getCustomerAccountClosingBalancesAction,
    onUpdateOverdraftStatus,
    overdraftUpdateInProgress,
    balancesIsLoading,
    balancesIsFetching,
    closed,
    setClosePopupVisible,
    // selectedAccountId,
    isLoading,
    isFetching,
    getChangeLogsAction,
    blockWithdrawPopupVisible,
    blockTopupPopupVisible,
    setComment,
    closePopupVisible,
    comment,
  };
};

export default useAccount;
